import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material'
import { AnySelectProps } from './AnySelect.types'
import { AnyComponentRef } from './AnyComponent.types'
import { TEXT_REQUIRED_VALIDATOR } from '../validators'

function AnySelect({
    variant = 'outlined',
    size = 'small',
    margin = 'dense',
    fullWidth = true,
    name,
    initialValue = '',
    label,
    options,
    required,
    disabled,
    register,
    notifier,
}: AnySelectProps) {
    const [value, setValue] = useState<string>(initialValue ?? '')
    const [blured, setBlured] = useState(false)
    const [valid, setValid] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [error, setError] = useState<string>()

    const ref = useRef<AnyComponentRef<string>>({
        valid,
        dirty,
        value,
        setBlur: () => setBlured(true),
    })

    const handleChange = useCallback((event: SelectChangeEvent<string>) => {
        setValue(event.target.value)
        setDirty(true)
    }, [])

    const onBlur = useCallback(() => {
        setBlured(true)
    }, [])

    const onNotify = useCallback(
        (value?: string) => {
            if (notifier) {
                notifier(name, value)
            }
        },
        [notifier, name]
    )

    useEffect(() => {
        const valid = !required || TEXT_REQUIRED_VALIDATOR.isValid(value)
        const error = valid ? undefined : TEXT_REQUIRED_VALIDATOR.getMessage()

        ref.current.valid = valid
        ref.current.dirty = dirty
        ref.current.value = value

        setValid(valid)
        setError(error)

        onNotify(value)
    }, [value, dirty, required, onNotify])

    useEffect(() => {
        register(name, ref.current)
    }, [register, name, ref])

    const labelId = `select-label-${name}`

    return (
        <FormControl
            variant={variant}
            margin={margin}
            size={size}
            required={required}
            fullWidth={fullWidth}
            error={blured && !valid}
        >
            <InputLabel id={labelId}>{label}</InputLabel>
            <Select
                labelId={labelId}
                label={label}
                value={value}
                required={required}
                disabled={disabled}
                onChange={handleChange}
                onBlur={onBlur}
                inputProps={{ name }}
            >
                {options.map((option) => (
                    <MenuItem key={option.value} value={option.value} dense>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
            {blured && !valid && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
    )
}

export default memo(AnySelect)
